/* Core & libraries */
import React, {useEffect, useMemo, useRef, useState} from "react";
import { useTheme } from "react-jss";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { ErrorMessage, Field, Formik } from "formik";
import {Row, Col, OverlayTrigger, Tooltip} from "react-bootstrap";
import { useNavigate } from "react-router";
import moment from 'helpers/moment';

/* Helpers, constants, custom methods, internal libraries */
import {
  useValidationService,
  createValidationPolicyYupSchema
} from "../../../resources/validations/validations";
import { usePeopleService } from "../../../services/peopleService";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useUserContext } from "../../../contexts/userContext";
import { UPDATE_PEOPLE_NAME_ENDPOINT } from "endpoints/endpoints";
import { PEOPLE_ROUTE } from "constants/routes";

/* Components */
import UCSelect from "../../ucselect";
import UCButton from "../../buttons";
import { ReactComponent as HelpIcon } from "../../../resources/images/help.svg";
import UCInputModal from "components/ucInputModal";

/* Styles */
import styles from "./styles.js"
import { colors } from "../../../constants/colors";
import generalStyles from "../../../resources/styles/general";

const messages = defineMessages({
  firstNamePlaceholder: {
    id: 'traveler.details.firstNamePlaceholder'
  },
  lastNamePlaceholder: {
    id: 'traveler.details.lastNamePlaceholder'
  },
  emailPlaceholder: {
    id: 'traveler.details.emailPlaceholder'
  },
  employeeIdPlaceholder: {
    id: 'traveler.details.employeeIdPlaceholder.UC'
  },
})

const UserProfileUC = ({travelerDetails, roles, teams, refetchTravelerDetails}) => {
  const theme = useTheme();
  const classes = styles({theme});
  const general = generalStyles({ theme });
  const intl = useIntl();
  const navigate = useNavigate();

  const formRef = useRef(null);

  const { travelerDetailsMockedApiValidationObj } = useValidationService()
  const [travelerSchema, setTravelerSchema] = useState({});

  const { updatePeople } = usePeopleService();
  const { checkPermission } = useHasPermission();
  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  useEffect(() => {
    (async () => {
      //TODO: api call to get validations //simpleMockedApiValidationObj
      const result = createValidationPolicyYupSchema(travelerDetailsMockedApiValidationObj);
      setTravelerSchema(result)
    })();
  }, []);

  const goBack = () => {
    navigate(PEOPLE_ROUTE);
  }

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit()
    }
  }

  const submitForm = async (values) => {
    await updatePeople({
      userId: travelerDetails?.userEntityId,
      roleId: values?.roleId,
      groupEntityId: values?.groupId,
    })
  }

  const updatePeopleEndpoint = useMemo(() => UPDATE_PEOPLE_NAME_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{userId}", travelerDetails.userEntityId), [providerCode, travelerDetails.userEntityId]);

  return (
    <div className={classes.personalInfoContainer}>
      {
        <Formik
          innerRef={formRef}
          enableReinitialize={true}
          initialValues={{
            firstName: travelerDetails?.firstName,
            lastName: travelerDetails?.lastName,
            roleId: travelerDetails?.companyInfo?.roleId,
            groupId: travelerDetails?.companyInfo?.groupEntityId,
            emailAddress: travelerDetails?.emailAddress,
            employeeId: travelerDetails?.employeeId
          }}
          validationSchema={travelerSchema}
          onSubmit={(values, { setSubmitting }) => {
            submitForm(values, setSubmitting);
          }}
        >
          {({
              values,
              errors,
              touched,
              setFieldTouched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue
            }) => {
            return (
              <>
                <form onSubmit={handleSubmit}>
                  <div className="row formDetailsBlock">
                    <div className="col-12 col-md-4 descText">
                      <div className={classes.personalDetails}>
                        <FormattedMessage id="traveler.details.personal.UC"/>
                      </div>
                      <div className={classes.description}>
                        <FormattedMessage id="traveler.details.descr.UC"/>
                      </div>
                    </div>
                    <div className="col-12 col-md-8">
                      <div className={classes.formContainer}>
                        <Row>
                          <Col xs={12} sm={6}>
                            <div className={`${classes.inputContainer} ${classes.rolesWrapper}`}>
                              <label htmlFor="status" className={classes.label}>
                                <FormattedMessage id="traveler.details.firstName"/>
                              </label>
                              <Field
                                placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
                                type="text"
                                name="firstName"
                                id="firstName"
                                className={
                                  errors.firstName && touched.firstName
                                    ? "input-error"
                                    : null
                                }
                                disabled={true}
                                as={UCInputModal}
                                travelerDetails={travelerDetails}
                                editUrlEndpoint={updatePeopleEndpoint}
                                method="PUT"
                                refetch={refetchTravelerDetails}
                                permission="F_EDIT_USER_NAMES"
                              />
                              <ErrorMessage
                                name="firstName"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                          </Col>
                          <Col xs={12} sm={6}>
                            <div className={`${classes.inputContainer} ${classes.rolesWrapper}`}>
                              <label htmlFor="status" className={classes.label}>
                                <FormattedMessage id="traveler.details.lastName"/>
                              </label>
                              <Field
                                placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
                                type="text"
                                name="lastName"
                                id="lastName"
                                className={
                                  errors.lastName && touched.lastName
                                    ? "input-error"
                                    : null
                                }
                                disabled={true}
                                as={UCInputModal}
                                travelerDetails={travelerDetails}
                                editUrlEndpoint={updatePeopleEndpoint}
                                method="PUT"
                                refetch={refetchTravelerDetails}
                                permission="F_EDIT_USER_NAMES"
                              />
                              <ErrorMessage
                                name="lastName"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                          </Col>
                        </Row>
                        {travelerDetails?.employeeId &&
                          <Row>
                            <div className={`${classes.inputContainer} ${classes.rolesWrapper}`}>
                              <label htmlFor="status" className={classes.label}>
                                <FormattedMessage id="traveler.details.employeeId.UC"/>
                              </label>
                              <Field
                                placeholder={intl.formatMessage(messages.employeeIdPlaceholder)}
                                type="text"
                                name="employeeId"
                                id="employeeId"
                                className={
                                  errors.employeeId && touched.employeeId
                                    ? "input-error"
                                    : null
                                }
                                disabled={true}
                              />
                              <ErrorMessage
                                name="employeeId"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                          </Row>
                        }
                        <Row>
                          <div className={classes.inputContainer}>
                            <label htmlFor="status" className={classes.label}>
                              <FormattedMessage id="traveler.details.emailAddress.UC"/>
                            </label>
                            <Field
                              placeholder={intl.formatMessage(messages.emailPlaceholder)}
                              type="text"
                              name="emailAddress"
                              id="emailAddress"
                              className={
                                errors.emailAddress && touched.emailAddress
                                  ? "input-error"
                                  : null
                              }
                              disabled={true}
                            />
                            <ErrorMessage
                              name="emailAddress"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                        </Row>
                      </div>
                      <div className={classes.sourceDetails}><FormattedMessage id="traveler.details.sourceDetails"/></div>
                    </div>
                  </div>

                  <div className={classes.dividerContainer}>
                    <hr className={classes.border}/>
                  </div>

                  <div className="row formDetailsBlock">
                    <div className="col-12 col-md-4 descText">
                      <div className={classes.personalDetails}>
                        <FormattedMessage id="traveler.details.membership.UC"/>
                      </div>
                      <div className={classes.description}>
                        <FormattedMessage id="traveler.details.desc.UC"/>
                      </div>
                    </div>
                    <div className="col-12 col-md-8">
                      <div className={classes.formContainer}>
                        <Row>
                          <div className={classes.inputContainer}>
                            <label htmlFor="status" className={classes.label}>
                              <FormattedMessage id="traveler.details.role"/>
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id="tooltipRole" className={`${general.main} ${classes.tooltip}`}>
                                    <div className={classes.roleTooltipTitle}><FormattedMessage id="traveler.details.roleTooltipTitle" /></div>
                                    <div className={classes.roleTooltipAdmin}><FormattedMessage id={`traveler.details.roleTooltipAdmin.${providerCode}`} /></div>
                                    <div className={classes.roleTooltipItem}><FormattedMessage id={`traveler.details.roleTooltipTraveler.${providerCode}`}/></div>
                                  </Tooltip>
                                }
                              >
                                <HelpIcon className={classes.helpIcon}/>
                              </OverlayTrigger>
                            </label>
                            {
                              roles.length > 0 &&
                              <Field
                                type="select"
                                name="roleId"
                                id="roleId"
                                render={({ form }) => (
                                  <div className={classes.rolesWrapper}>
                                    <UCSelect
                                      className={classes.roles}
                                      displayName={<FormattedMessage id="people.allRoles" />}
                                      options={roles}
                                      defaultValue={roles.find(item => parseInt(item.roleId) === parseInt(values.roleId))}
                                      onChange={(item) => form.setFieldValue("roleId", item.roleId)}
                                      getOptionLabel={(option) => option.roleName}
                                      getOptionValue={(option) => option.roleId}
                                      disabled={!travelerDetails?.canChangeRole || travelerDetails?.userStatus === "REMOVED" }
                                    />
                                    <ErrorMessage
                                      name="roleId"
                                      component="span"
                                      className={classes.error}
                                    />
                                  </div>
                                )}
                              />
                            }
                          </div>
                        </Row>
                        <Row>
                          <div className={classes.inputContainer}>
                            <label htmlFor="status" className={classes.label}>
                              <FormattedMessage id="traveler.details.team"/>
                            </label>
                            {
                              teams.length > 0 &&
                              <Field
                                type="select"
                                name="groupId"
                                id="groupId"
                                render={({ form }) => (
                                  <div>
                                    <UCSelect
                                      className={classes.roles}
                                      displayName={<FormattedMessage id="people.allTeams" />}
                                      options={teams}
                                      defaultValue={teams.find(item => item.groupEntityId === values.groupId)}
                                      onChange={(item) => form.setFieldValue("groupId", item.groupEntityId)}
                                      getOptionLabel={option => option.groupName}
                                      getOptionValue={option => option.groupEntityId}
                                      disabled={true}
                                    />
                                    <ErrorMessage
                                      name="groupId"
                                      component="span"
                                      className={classes.error}
                                    />
                                  </div>
                                )}
                              />
                            }
                          </div>
                        </Row>
                      </div>
                    </div>
                  </div>
                  <div className={classes.dividerContainer}>
                    <hr className={classes.border}/>
                  </div>
                  {
                    travelerDetails?.userStatus === "REMOVED" &&
                    <Row>
                      <Col>
                        <div className={classes.btnContainer}>
                          {
                            travelerDetails?.userStatus === "REMOVED" &&
                            <div className={classes.removedDate}>
                              <FormattedMessage id="traveler.details.removedOnDate" values={{date: moment(travelerDetails?.userStatusChangedOn).format("MM/DD/YYYY")}}/>
                            </div>
                          }
                        </div>
                      </Col>
                    </Row>
                  }
                  <Row>
                    <Col>
                      <div className={classes.buttonContainer}>
                        <div className={classes.btnContainer}>
                          <UCButton
                            type="default"
                            className={classes.button}
                            text={<FormattedMessage id="traveler.details.cancel"/>}
                            onClick={goBack}
                          />
                          {
                            travelerDetails?.userStatus === "ACTIVE" &&
                            (checkPermission("F_EDIT_USER") ||
                              (travelerDetails?.userEntityId === userDetails?.userInfo?.userEntityId && checkPermission("F_EDIT_MYUSER"))
                            ) &&
                            <UCButton
                              type="primary"
                              onClick={handleFormSubmit}
                              text={<FormattedMessage id="traveler.details.save"/>}
                              spinner={{
                                name: "updatePeopleSpinner",
                                size: 20,
                                color: colors.white
                              }}
                            />
                          }
                        </div>
                      </div>
                    </Col>
                  </Row>
                </form>
              </>
            )
          }
          }
        </Formik>
      }
    </div>
  )
}

export default UserProfileUC;